import './ManageBotSeating.styles.scss';

import { observer } from 'mobx-react';
import { useDebounce } from 'primereact/hooks';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import LobbyTable from '../../../components/Dashboard/LobbyTable/LobbyTable';
import ManageBotSeatingMenu from '../../../components/Dashboard/ManageBotSeatingMenu/ManageBotSeatingMenu';
import BlockLoader from '../../../components/Loading/BlockLoader/BlockLoader';
import {
  REFRESH_LOBBY_TABLES_INTERVAL,
  REFRESH_LOBBY_TABLES_INTERVAL_EXPERT_MODE,
} from '../../../constants';
import useScreenDimensions from '../../../hooks/useScreenDimensions';
import { LobbyTablePage } from '../../../model/lobbytable/LobbyTableResponse';
import expertModeStore from '../../../store/ExpertModeStore';
import lobbyTableStore from '../../../store/LobbyTableStore';
import userSettingsStore from '../../../store/UserSettingsStore';
import { getDashboardCardsContainerDimensions } from '../Dashboard.render.utils';
import { ManageBotSeatingProps } from './ManageBotSeating.types';

const DEFAULT_LOBBY_TABLE_PAGE: LobbyTablePage = {
  content: [],
  filterOptions: {
    clubs: [],
    leagues: [],
    superLeagues: [],
    gameTypes: [],
    limits: [],
    onlyAvailable: false,
  },
  size: 0,
  number: 0,
  totalElements: 0,
  last: true,
};

const LOBBY_TABLE_MIN_WIDTH = 460;
const LOBBY_TABLE_HEIGHT = 140;

const ManageBotSeating: FC<ManageBotSeatingProps> = ({ selectedClub }) => {
  const [isLoading, setLoading] = useState(true);

  const [tablesContainerHeight, setTablesContainerHeight] = useState(0);

  const [
    tablesPerPageCount,
    debouncedTablesPerPageCount,
    setTablesPerPageCount,
  ] = useDebounce(0, 300);

  const [lobbyTableWidth, setLobbyTableWidth] = useState(LOBBY_TABLE_MIN_WIDTH);

  const [currentPage, setCurrentPage] = useState(0);
  const [first, setFirst] = useState(0);

  const lobbyTablesContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const paginatorRef = useRef<Paginator>(null);

  const { screenWidth, screenHeight, scrollbarWidth } = useScreenDimensions();

  const [lobbyTablePage, setLobbyTablePage] = useState<LobbyTablePage>(
    DEFAULT_LOBBY_TABLE_PAGE,
  );

  const fetchData = (refresh = false): void => {
    if (!refresh) {
      setLoading(true);
    }
    lobbyTableStore
      .getLobbyTablePage(currentPage, debouncedTablesPerPageCount, selectedClub)
      .then(setLobbyTablePage)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (
      lobbyTablesContainerRef.current &&
      menuRef.current &&
      paginatorRef.current.getElement()
    ) {
      const calculatedScreenHeight =
        selectedClub && userSettingsStore.settings.table.expertMode
          ? screenHeight * expertModeStore.getContainerHeightCoefficient()
          : screenHeight;
      const { maxCardsCount, containerHeight, cardWidth } =
        getDashboardCardsContainerDimensions(
          LOBBY_TABLE_MIN_WIDTH,
          LOBBY_TABLE_HEIGHT,
          calculatedScreenHeight,
          lobbyTablesContainerRef.current.offsetWidth,
          menuRef.current.offsetHeight,
          paginatorRef.current.getElement().offsetHeight,
        );
      setTablesContainerHeight(containerHeight);
      setTablesPerPageCount(maxCardsCount);
      setLobbyTableWidth(cardWidth);
    }
  }, [
    screenWidth,
    screenHeight,
    scrollbarWidth,
    selectedClub,
    userSettingsStore.settings.table.expertMode,
  ]);

  useEffect(() => {
    let interval: NodeJS.Timer;
    const refreshInterval =
      selectedClub && userSettingsStore.settings.table.expertMode
        ? REFRESH_LOBBY_TABLES_INTERVAL_EXPERT_MODE
        : REFRESH_LOBBY_TABLES_INTERVAL;
    if (debouncedTablesPerPageCount) {
      fetchData();
      interval = setInterval(() => fetchData(true), refreshInterval);
    }
    return () => clearInterval(interval);
  }, [
    lobbyTableStore.searchFilter,
    lobbyTableStore.sorting.sortProperty,
    lobbyTableStore.sorting.sortDirection,
    debouncedTablesPerPageCount,
    currentPage,
    userSettingsStore.settings.table.expertMode,
    selectedClub,
  ]);

  const updatePagination = (e: PaginatorPageChangeEvent): void => {
    setCurrentPage(e.page);
    setFirst(e.first);
  };

  const onWaitListPlayersUpdate = useCallback(
    (tableId: string, playersCount: number) =>
      setLobbyTablePage(prevState => {
        const updatedContent = prevState.content.map(lobbyTable => {
          if (lobbyTable.tableId === tableId) {
            return { ...lobbyTable, waitlistCount: playersCount };
          }
          return lobbyTable;
        });
        return { ...prevState, content: updatedContent };
      }),
    [lobbyTablePage],
  );

  return (
    <div className="ManageBotSeating-Component">
      <ManageBotSeatingMenu
        ref={menuRef}
        filterOptions={lobbyTablePage.filterOptions}
        selectedClub={selectedClub}
      />
      <div
        className="lobby-tables-wrapper row align-content-start w-100 m-auto position-relative"
        ref={lobbyTablesContainerRef}
        style={{ height: tablesContainerHeight }}
      >
        <BlockLoader isLoading={isLoading} />
        {!isLoading &&
          lobbyTablePage.content.map(lobbyTable => (
            <LobbyTable
              key={`${lobbyTable.tableId}-${lobbyTable.club}-${lobbyTable.league}`}
              width={lobbyTableWidth}
              height={LOBBY_TABLE_HEIGHT}
              lobbyTable={lobbyTable}
              onWaitListPlayersUpdate={onWaitListPlayersUpdate}
            />
          ))}
        {!isLoading && !lobbyTablePage.content.length && (
          <div className="empty-results">
            <i className="pi pi-info-circle d-block" />
            <span className="d-block">No tables found</span>
          </div>
        )}
      </div>
      <Paginator
        first={first}
        alwaysShow
        ref={paginatorRef}
        rows={lobbyTablePage.size}
        totalRecords={lobbyTablePage.totalElements}
        onPageChange={updatePagination}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={
          lobbyTablePage.content.length ? 'Total pages: {totalPages}' : ''
        }
      />
    </div>
  );
};

export default observer(ManageBotSeating);
